// --- START locale formatting helpers ---
import i18n from '@/i18n/config';

/**
 * Get the current locale for Intl formatters
 * Maps i18next language codes to Intl-compatible locale codes
 */
function getCurrentLocale(): string {
  const lang = i18n.language || 'en';
  // Map i18next language to Intl locale - be explicit about pt-PT
  if (lang === 'pt-PT') {
    return 'pt-PT';
  }
  return 'en-US';
}

/**
 * Format a number with locale-specific formatting
 * @param value - Number to format
 * @param options - Intl.NumberFormatOptions
 */
export function formatNumber(
  value: number,
  options?: Intl.NumberFormatOptions
): string {
  const locale = getCurrentLocale();
  return new Intl.NumberFormat(locale, options).format(value);
}

/**
 * Format currency with Euro symbol and locale-specific formatting
 * @param value - Amount to format
 * @param currency - Currency code (default: EUR)
 */
export function formatCurrency(
  value: number,
  currency: string = 'EUR'
): string {
  const locale = getCurrentLocale();
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
  }).format(value);
}

/**
 * Format date in "DD Mon YYYY, HH:mm" format with localized month names
 * Example: "14 Dec 2025, 12:30" (en) or "14 Dez 2025, 12:30" (pt-PT)
 * @param date - Date to format
 */
export function formatDate(
  date: Date | string | number
): string {
  const locale = getCurrentLocale();
  const dateObj = typeof date === 'string' || typeof date === 'number' 
    ? new Date(date) 
    : date;
  
  // Get day, month, year, hours, minutes
  const day = dateObj.getDate().toString().padStart(2, '0');
  const year = dateObj.getFullYear();
  const hours = dateObj.getHours().toString().padStart(2, '0');
  const minutes = dateObj.getMinutes().toString().padStart(2, '0');
  
  // Get localized month abbreviation
  const monthFormatter = new Intl.DateTimeFormat(locale, { month: 'short' });
  let month = monthFormatter.format(dateObj);
  
  // For PT-PT, capitalize and remove period (dez. -> Dez)
  if (locale === 'pt-PT') {
    month = month.charAt(0).toUpperCase() + month.slice(1).replace('.', '');
  }
  
  return `${day} ${month} ${year}, ${hours}:${minutes}`;
}

/**
 * Format date with custom Intl options for more flexibility
 * @param date - Date to format
 * @param options - Intl.DateTimeFormatOptions (optional)
 */
export function formatDateCustom(
  date: Date | string | number,
  options?: Intl.DateTimeFormatOptions
): string {
  const locale = getCurrentLocale();
  const dateObj = typeof date === 'string' || typeof date === 'number' 
    ? new Date(date) 
    : date;
  
  return new Intl.DateTimeFormat(locale, options).format(dateObj);
}

/**
 * Format a percentage with locale-specific formatting
 * @param value - Number to format as percentage (0.15 = 15%)
 * @param decimals - Number of decimal places (default: 0)
 */
export function formatPercent(
  value: number,
  decimals: number = 0
): string {
  const locale = getCurrentLocale();
  return new Intl.NumberFormat(locale, {
    style: 'percent',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}
// --- END locale formatting helpers ---
